import { create } from "zustand";
import type { CellAssignment, GridPreset, Subtype } from "../types";

interface Snapshot {
  preset: GridPreset;
  cells: CellAssignment[];
  maximizedCellId: string | null;
}

interface GridState {
  preset: GridPreset;
  cells: CellAssignment[];
  maximizedCellId: string | null;
  preMaxSnapshot: Snapshot | null;
  selectedCellId: string | null;
  // false = object-contain (aspect ratio preservado, bandas negras).
  // true  = object-fill (estira el video al tamano de la casilla, deforma).
  fillCells: boolean;

  setPreset: (preset: GridPreset) => void;
  assignChannel: (cellId: string, deviceId: number, channel: number, subtype?: Subtype) => void;
  setStream: (cellId: string, streamId: string | null, streamName: string | null) => void;
  setSubtype: (cellId: string, subtype: Subtype, manual?: boolean) => void;
  setAllSubtype: (subtype: Subtype, manual?: boolean) => void;
  clearCell: (cellId: string) => void;
  clearAll: () => void;
  reset: () => void;
  maximize: (cellId: string) => void;
  restore: () => void;
  isMaximized: () => boolean;
  toggleFillCells: () => void;
  setSelectedCell: (cellId: string | null) => void;
  setGuardPage: (entries: Array<{ deviceId: number; channel: number } | null>) => void;
  restoreSnapshot: (snap: { preset: GridPreset; cells: CellAssignment[] }) => void;
}

function buildEmptyCells(preset: GridPreset): CellAssignment[] {
  // Distribucion en cuadricula simple. preset = N x N celdas tipicamente.
  // Usamos columnas = ceil(sqrt(preset)).
  const cols = Math.ceil(Math.sqrt(preset));
  return Array.from({ length: preset }).map((_, i) => ({
    cellId: `c-${i}`,
    deviceId: null,
    channel: null,
    subtype: 1,
    streamId: null,
    streamName: null,
    manualSubtype: null,
    x: i % cols,
    y: Math.floor(i / cols),
    w: 1,
    h: 1,
  }));
}

export function autoSubtypeForPreset(preset: GridPreset): Subtype {
  if (preset <= 4) return 0;       // main
  return 1;                         // sub
}

export const useGridStore = create<GridState>((set, get) => ({
  preset: 4,
  cells: buildEmptyCells(4),
  maximizedCellId: null,
  preMaxSnapshot: null,
  selectedCellId: null,
  fillCells: false,

  setPreset: (preset) => {
    if (get().maximizedCellId) return;
    const old = get().cells;
    const next = buildEmptyCells(preset);
    // mantener asignaciones de las primeras celdas
    for (let i = 0; i < Math.min(old.length, next.length); i++) {
      next[i] = {
        ...next[i],
        deviceId: old[i].deviceId,
        channel: old[i].channel,
        subtype: old[i].manualSubtype ?? autoSubtypeForPreset(preset),
        streamId: old[i].streamId,
        streamName: old[i].streamName,
        manualSubtype: old[i].manualSubtype,
      };
    }
    set({ preset, cells: next });
  },

  assignChannel: (cellId, deviceId, channel, subtype) => {
    set((s) => ({
      cells: s.cells.map((c) =>
        c.cellId === cellId
          ? { ...c, deviceId, channel, subtype: subtype ?? c.manualSubtype ?? autoSubtypeForPreset(s.preset) }
          : c,
      ),
    }));
  },

  setStream: (cellId, streamId, streamName) => {
    set((s) => ({
      cells: s.cells.map((c) =>
        c.cellId === cellId ? { ...c, streamId, streamName } : c,
      ),
    }));
  },

  setSubtype: (cellId, subtype, manual = false) => {
    set((s) => ({
      cells: s.cells.map((c) =>
        c.cellId === cellId
          ? { ...c, subtype, manualSubtype: manual ? subtype : c.manualSubtype }
          : c,
      ),
    }));
  },

  // Aplica el subtype a TODAS las celdas con canal asignado. Se usa para los
  // toggles "Main Stream" / "Sub Stream" de la barra superior. `manual=true`
  // por default para que useAutoSubtype NO sobrescriba la decision al cambiar
  // de preset; el usuario puede revertir con click derecho > Auto si lo quisiera.
  setAllSubtype: (subtype, manual = true) => {
    set((s) => ({
      cells: s.cells.map((c) =>
        c.deviceId != null && c.channel != null
          ? { ...c, subtype, manualSubtype: manual ? subtype : c.manualSubtype }
          : c,
      ),
    }));
  },

  clearCell: (cellId) => {
    set((s) => ({
      cells: s.cells.map((c) =>
        c.cellId === cellId
          ? { ...c, deviceId: null, channel: null, streamId: null, streamName: null, manualSubtype: null }
          : c,
      ),
      selectedCellId: s.selectedCellId === cellId ? null : s.selectedCellId,
    }));
  },

  clearAll: () => {
    set({ cells: buildEmptyCells(get().preset), maximizedCellId: null, preMaxSnapshot: null, selectedCellId: null });
  },

  reset: () => {
    set({
      preset: 4,
      cells: buildEmptyCells(4),
      maximizedCellId: null,
      preMaxSnapshot: null,
      selectedCellId: null,
    });
  },

  maximize: (cellId) => {
    const s = get();
    if (s.maximizedCellId === cellId) {
      // ya esta maximizada -> restaurar
      get().restore();
      return;
    }
    if (s.maximizedCellId) {
      // ya esta otra maximizada, saltar (deberian restaurar primero)
      return;
    }
    const snap: Snapshot = {
      preset: s.preset,
      cells: structuredClone(s.cells),
      maximizedCellId: null,
    };
    // forzar subtype 0 en la celda maximizada
    const newCells: CellAssignment[] = s.cells.map((c) =>
      c.cellId === cellId ? { ...c, subtype: 0 as Subtype } : c,
    );
    set({ preMaxSnapshot: snap, maximizedCellId: cellId, cells: newCells });
  },

  restore: () => {
    const s = get();
    if (!s.preMaxSnapshot) {
      set({ maximizedCellId: null });
      return;
    }
    set({
      preset: s.preMaxSnapshot.preset,
      cells: s.preMaxSnapshot.cells,
      maximizedCellId: null,
      preMaxSnapshot: null,
    });
  },

  isMaximized: () => get().maximizedCellId !== null,

  toggleFillCells: () => set((s) => ({ fillCells: !s.fillCells })),

  setSelectedCell: (cellId) => set({ selectedCellId: cellId }),

  // Aplica una pagina completa de la Guardia Virtual en un solo set(). `entries`
  // esta alineado al indice de celda; entries[i] === null vacia esa celda. Si la
  // celda ya muestra ese deviceId+channel devuelve el MISMO objeto, asi su
  // GridCell no re-renderiza ni reinicia el stream (transicion sin parpadeo).
  setGuardPage: (entries) => {
    set((s) => ({
      cells: s.cells.map((c, i) => {
        const e = entries[i] ?? null;
        const wantDev = e?.deviceId ?? null;
        const wantCh = e?.channel ?? null;
        if (c.deviceId === wantDev && c.channel === wantCh) return c;
        if (wantDev == null || wantCh == null) {
          return { ...c, deviceId: null, channel: null, streamId: null, streamName: null, manualSubtype: null };
        }
        return {
          ...c,
          deviceId: wantDev,
          channel: wantCh,
          subtype: c.manualSubtype ?? autoSubtypeForPreset(s.preset),
          streamId: null,
          streamName: null,
        };
      }),
    }));
  },

  restoreSnapshot: (snap) => {
    set({
      preset: snap.preset,
      cells: snap.cells,
      maximizedCellId: null,
      preMaxSnapshot: null,
      selectedCellId: null,
    });
  },
}));
